import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders } from '../../api/core.js';
import { Mail, Download, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Email actions for workflows
 * Allows generating and downloading .eml files based on templates
 *
 * Text (title/description/template label) is intentionally NOT stored here: this object is a
 * module-level constant evaluated once at import time, before any language is known. Only
 * codes and i18n key fragments live here; the actual strings are resolved at render via t()
 * in getWorkflowConfig() below (common.email.* namespace).
 */
const WORKFLOW_EMAIL_CONFIG = {
  vehicle_entry: {
    i18nKey: 'vehicleEntry',
    templates: [
      { code: 'VEH_ACQUISITION', i18nKey: 'vehAcquisition', recipientType: 'manager' },
      { code: 'VEH_LEASING_CONFIRM', i18nKey: 'vehLeasingConfirm', recipientType: 'custom' },
      { code: 'VEH_BROKER_NOTIFY', i18nKey: 'vehBrokerNotify', recipientType: 'broker' }
    ]
  },
  vehicle_exit: {
    i18nKey: 'vehicleExit',
    templates: [
      { code: 'VEH_EXIT_CONFIRM', i18nKey: 'vehExitConfirm', recipientType: 'custom' },
      { code: 'VEH_EXIT_LEASING', i18nKey: 'vehExitLeasing', recipientType: 'custom' }
    ]
  },
  insurance_termination: {
    i18nKey: 'insuranceTermination',
    templates: [
      { code: 'INS_CANCEL_FLEET', i18nKey: 'insCancelFleet', recipientType: 'insurer' },
      { code: 'INS_CANCEL_INDIVIDUAL', i18nKey: 'insCancelIndividual', recipientType: 'insurer' },
      { code: 'INS_CLAIMS_HISTORY', i18nKey: 'insClaimsHistory', recipientType: 'insurer' },
      { code: 'INS_REFUND_REQUEST', i18nKey: 'insRefundRequest', recipientType: 'insurer' }
    ]
  }
};

/**
 * EmailActionsPanel Component
 *
 * @param {Object} props
 * @param {'vehicle_entry'|'vehicle_exit'} props.workflow - Workflow type
 * @param {'vehicle'|'driver'|'claim'} props.entityType - Entity type
 * @param {number|string} props.entityId - Entity ID
 * @param {Object} props.entityData - Entity data for display
 * @param {boolean} [props.compact=false] - Compact mode
 * @param {Function} [props.onEmailGenerated] - Callback when email is generated
 */
export default function EmailActionsPanel({
  workflow,
  entityType,
  entityId,
  entityData,
  compact = false,
  onEmailGenerated
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(!compact);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [preview, setPreview] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [error, setError] = useState('');

  const rawConfig = WORKFLOW_EMAIL_CONFIG[workflow];
  if (!rawConfig) return null;

  // Resolve title/description/template labels at render time (see comment on
  // WORKFLOW_EMAIL_CONFIG above) so a language switch updates them immediately.
  const config = {
    title: t(`common.email.title.${rawConfig.i18nKey}`),
    description: t(`common.email.description.${rawConfig.i18nKey}`),
    templates: rawConfig.templates.map((tpl) => ({
      ...tpl,
      label: t(`common.email.templates.${tpl.i18nKey}.label`),
      description: t(`common.email.templates.${tpl.i18nKey}.description`)
    }))
  };

  // Load preview when template selected
  const handleTemplateSelect = async (template) => {
    setSelectedTemplate(template);
    setPreview(null);
    setError('');

    // For now, show a preview with entity data
    // In production, this would call the API to resolve variables
    setPreview({
      subject: `${template.label} - ${entityData?.license_plate || entityData?.make || t('common.email.preview.vehicleLabel')}`,
      body: `${t('common.email.preview.templateLabel')}: ${template.code}\n${t('common.email.preview.vehicleLabel')}: ${entityData?.make} ${entityData?.model}\n${t('common.email.preview.plateLabel')}: ${entityData?.license_plate}`
    });
  };

  // Generate and download EML
  const handleGenerateEmail = async () => {
    if (!selectedTemplate || !entityId) return;

    setLoading(true);
    setError('');

    try {
      // Build the download URL
      const recipient = recipientEmail || 'destinataire@example.com';
      const url = `/api/v1/email_templates/${selectedTemplate.code}/download/${entityType}/${entityId}?email=${encodeURIComponent(recipient)}`;

      // Fetch the EML file (getAuthHeaders : jeton + tenant + X-Active-Org)
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(t('common.email.errors.generateFailed'));
      }

      // Get filename from response headers or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `${selectedTemplate.code}_${entityId}.eml`;

      // Download the file
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      onEmailGenerated?.({ template: selectedTemplate, filename });
    } catch (err) {
      setError(err.message || t('common.email.errors.generateFailedShort'));
    } finally {
      setLoading(false);
    }
  };

  if (compact && !expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="flex items-center gap-2 text-sm text-amber-600 hover:text-amber-700"
      >
        <Mail className="w-4 h-4" />
        {t('common.email.generateButton')}
        <ChevronDown className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="border border-amber-200 rounded-lg bg-amber-50 overflow-hidden">
      {/* Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 ${compact ? 'cursor-pointer hover:bg-amber-100' : ''}`}
        onClick={compact ? () => setExpanded(!expanded) : undefined}
      >
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-amber-600" />
          <h4 className="font-medium text-amber-900">{config.title}</h4>
        </div>
        {compact && (
          <button className="text-amber-600">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          <p className="text-sm text-amber-800">{config.description}</p>

          {/* Template Selection */}
          <div className="grid grid-cols-1 gap-2">
            {config.templates.map((template) => (
              <button
                key={template.code}
                onClick={() => handleTemplateSelect(template)}
                className={`flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
                  selectedTemplate?.code === template.code
                    ? 'bg-amber-100 border-2 border-amber-400'
                    : 'bg-white border border-amber-200 hover:border-amber-300'
                }`}
              >
                <Mail className={`w-5 h-5 mt-0.5 ${
                  selectedTemplate?.code === template.code ? 'text-amber-600' : 'text-gray-400'
                }`} />
                <div className="flex-1">
                  <span className="font-medium text-gray-900 text-sm block">{template.label}</span>
                  <span className="text-xs text-gray-500">{template.description}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Recipient Email */}
          {selectedTemplate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.email.recipientLabel')}
              </label>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="destinataire@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">{t('common.email.previewLabel')}</div>
              <div className="text-sm font-medium text-gray-900 mb-2">{preview.subject}</div>
              <div className="text-xs text-gray-600 font-mono whitespace-pre-wrap">{preview.body}</div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          {/* Generate Button */}
          {selectedTemplate && (
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setSelectedTemplate(null);
                  setPreview(null);
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                {t('common.actions.cancel')}
              </button>
              <button
                onClick={handleGenerateEmail}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 text-sm"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {t('common.email.downloadEml')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
